/**
 * usePrompt — a Promise-based prompt dialog hook.
 *
 * Replaces native `window.prompt()` with a styled, Playwright-friendly
 * modal. Returns the trimmed user input on submit, or `null` if the user
 * cancels / closes the modal.
 *
 * Usage:
 *
 *   // 1) Wrap the app in <PromptProvider> (already mounted in App.js).
 *   //
 *   // 2) In any component:
 *   const prompt = usePrompt();
 *   const onAddTask = async () => {
 *     const title = await prompt({
 *       title: "Add task",
 *       body: "Give the task a short name.",
 *       placeholder: "e.g. Send proposal",
 *       submitLabel: "Add task",
 *       testid: "add-task-prompt",
 *     });
 *     if (!title) return;
 *     doAddTask(title);
 *   };
 */
import { createContext, useCallback, useContext, useRef, useState } from "react";
import PromptDialog from "@/components/PromptDialog";

const PromptContext = createContext(null);

export function PromptProvider({ children }) {
  const [config, setConfig] = useState(null);
  const [busy, setBusy] = useState(false);
  const resolverRef = useRef(null);

  const close = useCallback((result) => {
    setConfig(null);
    setBusy(false);
    if (resolverRef.current) {
      const r = resolverRef.current;
      resolverRef.current = null;
      r(result);
    }
  }, []);

  const prompt = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setConfig({
        title: opts.title || "Enter value",
        body: opts.body || "",
        defaultValue: opts.defaultValue || "",
        placeholder: opts.placeholder || "",
        submitLabel: opts.submitLabel || "Save",
        required: opts.required !== false,
        testid: opts.testid || "use-prompt-dialog",
      });
    });
  }, []);

  const onSubmit = useCallback((value) => {
    setBusy(true);
    // Tiny microtask delay so disabled state paints before resolve fires.
    Promise.resolve().then(() => close(value));
  }, [close]);

  const onCancel = useCallback(() => close(null), [close]);

  return (
    <PromptContext.Provider value={prompt}>
      {children}
      {config && (
        <PromptDialog
          title={config.title}
          body={config.body}
          defaultValue={config.defaultValue}
          placeholder={config.placeholder}
          submitLabel={config.submitLabel}
          required={config.required}
          busy={busy}
          onSubmit={onSubmit}
          onCancel={onCancel}
          testid={config.testid}
        />
      )}
    </PromptContext.Provider>
  );
}

export default function usePrompt() {
  const ctx = useContext(PromptContext);
  if (!ctx) {
    // Fallback to window.prompt if the provider isn't mounted (e.g. unit
    // tests). Keeps the hook usable without a mandatory provider.
    return async (opts = {}) => {
      // eslint-disable-next-line no-alert
      const v = window.prompt(opts.title || "Enter value", opts.defaultValue || "");
      if (v == null) return null;
      const t = v.trim();
      if (opts.required !== false && !t) return null;
      return t;
    };
  }
  return ctx;
}
